import React, { useState, useEffect } from "react";
import {
  createLateFeeRule,
  updateLateFeeRule,
  createLateFeeRulePortal,
  updateLateFeeRulePortal,
} from "../../api/fees/feeApi";
import {
  Modal,
  Button,
  Input,
  Select,
  Textarea,
  Switch,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const PENALTY_TYPE_OPTIONS = [
  { label: "Flat amount (once per bill)", value: "FLAT" },
  { label: "Percentage of overdue due (once per bill)", value: "PERCENTAGE" },
];

const LateFeeRuleModal = ({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
  isPortal = false,
}) => {
  const [form, setForm] = useState({
    name: "",
    description: "",
    penaltyType: "FLAT",
    penaltyValue: 0,
    gracePeriodDays: 0,
    priority: 1,
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      setForm({
        name: editData.name || "",
        description: editData.description || "",
        penaltyType: editData.penaltyType || "FLAT",
        penaltyValue: editData.penaltyValue ?? 0,
        gracePeriodDays: editData.gracePeriodDays ?? 0,
        priority: editData.priority ?? 1,
        isActive: editData.isActive !== false,
      });
    } else {
      setForm({
        name: "",
        description: "",
        penaltyType: "FLAT",
        penaltyValue: 0,
        gracePeriodDays: 0,
        priority: 1,
        isActive: true,
      });
    }
  }, [isOpen, editData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        penaltyValue: Number(form.penaltyValue),
        gracePeriodDays: Number(form.gracePeriodDays),
        priority: Number(form.priority),
      };
      if (editData?._id) {
        if (isPortal) await updateLateFeeRulePortal(editData._id, payload);
        else await updateLateFeeRule(editData._id, payload);
        toast.success("Late fee rule updated.");
      } else {
        if (isPortal) await createLateFeeRulePortal(payload);
        else await createLateFeeRule(payload);
        toast.success("Late fee rule created.");
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editData ? "Edit Late Fee Rule" : "Add Late Fee Rule"}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="late-fee-rule-form"
            variant="primary"
            isLoading={loading}
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="late-fee-rule-form"
        onSubmit={handleSubmit}
        className="space-y-4 pt-1"
      >
        <p className="rounded-md border border-primary-100 bg-primary-50/60 px-3 py-2 text-xs text-neutral-700 dark:border-primary-900/40 dark:bg-primary-950/30 dark:text-neutral-300">
          Fine is applied <strong>once per fee bill</strong> (ledger), not once
          per fee head. A bill with 3 fee heads receives one late fee, not three.
        </p>

        <Input
          label="Name *"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="e.g. 5-Day Grace Period Fine"
          required
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Penalty Type"
            options={PENALTY_TYPE_OPTIONS}
            value={form.penaltyType}
            onChange={(val) => setForm((p) => ({ ...p, penaltyType: val }))}
          />

          <Input
            label={
              form.penaltyType === "PERCENTAGE"
                ? "Penalty Value (%)"
                : "Penalty Value (₹)"
            }
            type="number"
            min="0"
            step="0.01"
            name="penaltyValue"
            value={form.penaltyValue}
            onChange={handleChange}
            leftIcon={
              form.penaltyType === "FLAT" ? (
                <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                  ₹
                </span>
              ) : undefined
            }
            rightIcon={
              form.penaltyType === "PERCENTAGE" ? (
                <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                  %
                </span>
              ) : undefined
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Grace Period (days)"
            type="number"
            min="0"
            name="gracePeriodDays"
            value={form.gracePeriodDays}
            onChange={handleChange}
            helperText="Days after due date before the late fee starts. Editable anytime."
          />

          <Input
            label="Priority"
            type="number"
            min="1"
            name="priority"
            value={form.priority}
            onChange={handleChange}
            helperText="Lower number runs first when multiple rules match."
          />
        </div>

        <Textarea
          label="Description"
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={2}
          placeholder="Add details regarding late fee applicability..."
        />

        <div className="pt-1">
          <Switch
            label="Active"
            checked={form.isActive}
            onChange={(checked) =>
              setForm((p) => ({ ...p, isActive: checked }))
            }
          />
        </div>
      </form>
    </Modal>
  );
};

export default LateFeeRuleModal;